import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { FaRoute, FaRoad, FaMapMarkerAlt } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const EMPTY_FORM = {
  routeName: "",
  distance: "",
  from: "",
  to: "",
};

const MdlAddTransportRoute = ({ actionClose, onSuccess }) => {
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    setShow(true);
    setForm(EMPTY_FORM);
    setSaving(false);
  }, []);

  const handleClose = useCallback(() => {
    if (saving) return;
    setShow(false);
    if (typeof actionClose === "function") actionClose();
  }, [actionClose, saving]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const routeName = form.routeName.trim();
    const from = form.from.trim();
    const to = form.to.trim();
    const distance = String(form.distance).trim();

    if (!routeName || !from || !to || !distance) {
      myToast.notify_warning("Please fill all fields.");
      return;
    }

    const distanceNum = Number(distance);
    if (!Number.isFinite(distanceNum) || distanceNum < 0) {
      myToast.notify_warning("Enter a valid distance.");
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append("route_name", routeName);
      params.append("start_from", from);
      params.append("end_to", to);
      params.append("route_distance", distance);
      params.append("school_code", schoolCode);

      const response = await axios.post(
        `${API_BASE()}addNewRoute.php`,
        params
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Route added successfully");
        setShow(false);
        if (typeof onSuccess === "function") onSuccess();
        if (typeof actionClose === "function") actionClose();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not add route"
        );
      }
    } catch (error) {
      console.error("[MdlAddTransportRoute] add error", error);
      myToast.notify_error("Failed to add route");
    } finally {
      setSaving(false);
    }
  }, [form, actionClose, onSuccess]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      centered
      size="lg"
      className="mdl-add-route"
      backdrop={saving ? "static" : true}
      keyboard={!saving}
    >
      <Modal.Header closeButton={!saving} className="mdl-add-route__header">
        <Modal.Title className="mdl-add-route__title">
          <span className="mdl-add-route__icon" aria-hidden>
            <FaRoute />
          </span>
          Add New Route
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="mdl-add-route__body">
        {saving ? (
          <div className="mdl-add-route__loading">
            <MyLoader />
          </div>
        ) : (
          <div className="mdl-add-route__form">
            <div className="mdl-add-route__field mdl-add-route__field--full">
              <label htmlFor="mdl-route-name">Route name</label>
              <input
                id="mdl-route-name"
                name="routeName"
                type="text"
                placeholder="e.g. North Campus Loop"
                value={form.routeName}
                onChange={handleChange}
                autoFocus
              />
            </div>

            <div className="mdl-add-route__row">
              <div className="mdl-add-route__field">
                <label htmlFor="mdl-route-from">
                  <FaMapMarkerAlt aria-hidden /> From
                </label>
                <input
                  id="mdl-route-from"
                  name="from"
                  type="text"
                  placeholder="Start point"
                  value={form.from}
                  onChange={handleChange}
                />
              </div>
              <div className="mdl-add-route__field">
                <label htmlFor="mdl-route-to">
                  <FaMapMarkerAlt aria-hidden /> To
                </label>
                <input
                  id="mdl-route-to"
                  name="to"
                  type="text"
                  placeholder="End point"
                  value={form.to}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mdl-add-route__field">
              <label htmlFor="mdl-route-distance">
                <FaRoad aria-hidden /> Distance (km)
              </label>
              <input
                id="mdl-route-distance"
                name="distance"
                type="number"
                min="0"
                step="0.1"
                placeholder="e.g. 12.5"
                value={form.distance}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="mdl-add-route__footer">
        <Button
          variant="light"
          onClick={handleClose}
          disabled={saving}
          className="mdl-add-route__cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="mdl-add-route__submit"
          style={{
            background: COLORS.primary_color_shade5,
            borderColor: COLORS.primary_color_shade7,
          }}
        >
          {saving ? "Adding…" : "Add Route"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlAddTransportRoute;
